/**
 * Thin fetch wrapper for the GeoKerja admin backend.
 *
 * Auth post-Clerk-migration:
 *  - The bearer token is a Clerk session JWT.
 *  - We do *not* manage refresh ourselves — Clerk's SDK rotates the session
 *    transparently and `getToken()` always returns a current one.
 *  - Token retrieval is wired in once at app boot via `setTokenGetter`,
 *    called by the `<ClerkBridge>` component which has access to the
 *    Clerk react context.
 *
 * Anything outside React (services, tests) can still call `apiFetch`; it
 * just awaits whatever the token getter returns.
 */

// Fallback matches the fixed local backend port (8095); override via VITE_API_URL.
const API_BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8095/api/v1";

/** Async getter for the current bearer. Set by `<ClerkBridge>` at boot. */
let tokenGetter: () => Promise<string | null> = async () => null;

/** Called by ClerkBridge once the Clerk provider has mounted. */
export function setTokenGetter(fn: () => Promise<string | null>) {
  tokenGetter = fn;
}

/** Optional handler invoked when a request returns 401 with no token to
 *  retry. Kept as a no-op by default — `<SignedIn>` / `<SignedOut>` from
 *  Clerk handle the actual redirect at the route level. */
let onAuthFailed: (() => void) | null = null;
export function registerAuthFailureHandler(fn: () => void) {
  onAuthFailed = fn;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

type Options = Omit<RequestInit, "body"> & {
  body?: unknown;
  skipAuth?: boolean;
  /**
   * Don't treat a 401 from this call as "the session is gone".
   *
   * For identity *probes* — asking "is this Clerk user also an employee?" —
   * a 401 is a legitimate negative answer, not a dead session. Without this,
   * probing the employee endpoint while signed in as an admin would fire the
   * global auth-failure handler and log the admin out of their own lane.
   */
  suppressAuthFailure?: boolean;
};

export async function apiFetch<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const { skipAuth, suppressAuthFailure, body, headers, ...rest } = opts;
  const token = skipAuth ? null : await tokenGetter();

  const init: RequestInit = {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      ...(headers as Record<string, string> | undefined),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };

  const res = await fetch(`${API_BASE_URL}${path}`, init);

  if (res.status === 401 && !skipAuth && !suppressAuthFailure) {
    // Clerk has already rotated under the hood if it was going to; a 401
    // here means the session is genuinely gone (e.g. user signed out in
    // another tab, or the row was deactivated server-side).
    onAuthFailed?.();
  }

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json") ? await res.json() : await res.text();

  if (!res.ok) {
    const message =
      typeof payload === "object" && payload !== null && "error" in payload
        ? extractMessage((payload as { error: unknown }).error)
        : typeof payload === "string"
          ? payload
          : res.statusText;
    throw new ApiError(res.status, message || "Request failed", payload);
  }

  return payload as T;
}

/**
 * Authenticated fetch that returns the raw `Response`, for endpoints that
 * stream a file rather than return JSON (e.g. the tenant data export). Uses the
 * same bearer as `apiFetch`; the caller decides how to read the body.
 */
export async function apiFetchRaw(path: string, opts: RequestInit = {}): Promise<Response> {
  const token = await tokenGetter();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...opts,
    headers: {
      ...(opts.headers as Record<string, string> | undefined),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (res.status === 401) onAuthFailed?.();
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(res.status, body || res.statusText || "Request failed");
  }
  return res;
}

/**
 * Fetch an **absolute** GeoKerja API URL that 302-redirects to a signed asset
 * (request attachments, FEAT-2026-008) and return a local `blob:` object URL
 * for the bytes. We send the Clerk bearer to our API; the browser drops it on
 * the cross-origin redirect to Blob storage (which authorizes via the SAS in
 * the URL). Reading the storage response cross-origin needs the storage
 * account's CORS to allow this origin (configured in `provision-azure.sh`).
 *
 * The caller owns the returned URL — call `URL.revokeObjectURL` when done.
 */
export async function fetchSignedAsset(absoluteUrl: string): Promise<string> {
  const token = await tokenGetter();
  const res = await fetch(absoluteUrl, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText || "Failed to load attachment");
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

function extractMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const m = (error as { message?: unknown }).message;
    if (typeof m === "string") return m;
    if (Array.isArray(m)) return m.join(", ");
  }
  return "Request failed";
}
